import { publicApi } from "@api";
import { IconMSIPhoneAndroid } from "@assets";
import { FreeGiftsBadge } from "@components/data-display";
import { usePostLoginCheck } from "@components/utils/Auth/hooks";
import { OnboardCallbackProps } from "@components/utils/Auth/types";
import { envConfig } from "@configs/env";
import { SEON_FEATURE } from "@configs/seon";
import {
  OTPMethod,
  OTPSource,
  RegistrationMethod,
  useRegistrationGiftIndicators,
  useStatsigTracking,
} from "@configs/statsig";
import { OnboardMode, ValidatorSchema } from "@constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { useOnboardingPhone } from "@hooks/useOnboardingPhone";
import { usePostLoginHandler } from "@hooks/usePostLoginHandler";
import { useSeonSession } from "@hooks/useSeonSession";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { getStoredCredential } from "@utils/credentials";
import { resolveAuthResponseError } from "@utils/error-resolvers";
import { getOTPCodeSentMessage } from "@utils/message";
import {
  APIErrorCodes,
  Button,
  TextField,
  cn,
  delay,
  localizePhoneNumber,
  standardizePhoneNumber,
  useDateAvailCheck,
  useFieldHelperText,
  useHandleApiResponse,
} from "kz-ui-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { OnboardingModeRadioGroup } from "../../..";
import { AuthSocial } from "../../AuthSocial";

type LoginFormType = {
  phone: string;
};

interface OnboardPPProps extends OnboardCallbackProps {
  onLogin: (phone?: string) => void;
  onRegister: (phone?: string) => void;
}

const OnboardPP = ({ defaultMode = OnboardMode.LOGIN, onLogin, onRegister }: OnboardPPProps) => {
  const { t, i18n } = useTranslation();
  const { trackOTPSent, trackOTPRequestFailed, trackRegisterFormFocus, trackOTPRequest } = useStatsigTracking();
  const [mode, setMode] = useState<OnboardMode>(defaultMode);
  const [createRegisterMutation, { isLoading: isCreateRegisterLoading }] = publicApi.useCreateRegisterMutation();
  const { handleApiResponse } = useHandleApiResponse({ toast });
  const { updatePhone: updateOnboardingPhone } = useOnboardingPhone();
  const { phoneMinLength, phoneMaxLength, phonePrefix, phoneStartWith, phoneSimpleError, showPhoneInputPrefix } =
    useMemberSettings();
  useSeonSession(SEON_FEATURE.ONBOARDING, {
    autoInit: true,
  });
  const { loginFlowHandlers } = usePostLoginHandler({
    flowMode: "dialog",
  });
  const postLoginCheck = usePostLoginCheck({
    onLoginSuccess: loginFlowHandlers.handleLoginSuccess,
  });
  const { showFreeGiftsBadge } = useRegistrationGiftIndicators();

  const loginFormSchema = useMemo(() => {
    return z.object({
      phone: ValidatorSchema.phone({
        minLength: phoneMinLength,
        maxLength: phoneMaxLength,
        startWith: phoneStartWith,
        simpleError: phoneSimpleError,
        msgType: "phone",
      }),
    });
  }, [phoneMinLength, phoneMaxLength, phoneStartWith, phoneSimpleError]);

  // sync with defaultMode
  useEffect(() => {
    setMode(defaultMode);
  }, [defaultMode]);

  const loginCredId = localizePhoneNumber(getStoredCredential().clientId ?? "", phonePrefix);

  const {
    handleSubmit: handlePhoneSubmit,
    register: formPhoneRegister,
    formState: { errors: formPhoneErrors, submitCount },
    watch,
    trigger,
  } = useForm<LoginFormType>({
    mode: "onChange",
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      phone: loginCredId,
    },
  });

  const fieldHelperTextManager = useFieldHelperText({
    submitCount,
    trigger: trigger,
    errors: formPhoneErrors,
  });

  const { check: checkRequestOTP } = useDateAvailCheck(loginCredId);

  const submitting = useRef(false);
  const hasFocusedPhoneFormRef = useRef<boolean>(false);

  const onSubmitPhone = async ({ phone }: LoginFormType) => {
    if (submitting.current) return;
    submitting.current = true;

    let allowRedirect = true;

    updateOnboardingPhone(phone);

    if (mode === OnboardMode.REGISTER) {
      if (checkRequestOTP()) {
        trackOTPRequest(OTPMethod.SMS, { source: OTPSource.REGISTER });
        const createRegisterResponse = await createRegisterMutation({
          phone: standardizePhoneNumber(phone, phonePrefix),
          accountId: envConfig.accountId,
        });

        if (createRegisterResponse.error) {
          const errorData = (createRegisterResponse.error as FetchBaseQueryError).data as {
            error: { message: string };
          };
          const errorMessage = errorData?.error?.message;

          if (errorMessage === APIErrorCodes.VerifyRequest.RateLimited || errorMessage?.startsWith("unauthorized")) {
            allowRedirect = false;
          }
        }

        handleApiResponse(createRegisterResponse, {
          toastSuccess: false,
          onSuccess: () => {
            if (createRegisterResponse?.data?.method) {
              // Track OTP sent success
              trackOTPSent(createRegisterResponse.data.method, { source: OTPSource.REGISTER });

              toast.success(getOTPCodeSentMessage(createRegisterResponse.data.method, i18n.language, t), {
                duration: 8000,
              });
            }
          },
          toastError: false,
          onError: (error) => {
            const message = resolveAuthResponseError(error, t);
            trackOTPRequestFailed(error, { source: OTPSource.REGISTER });
            if (message) {
              toast.error(message);
            }
          },
        });
      }
    }
    submitting.current = false;

    if (allowRedirect) {
      if (mode === OnboardMode.LOGIN) {
        onLogin(phone);
      } else {
        onRegister(phone);
      }
    }
  };

  return (
    <>
      <OnboardingModeRadioGroup
        value={mode}
        onChange={setMode}
      />
      <div className="mt-6 flex w-full flex-col">
        <div className="flex flex-col items-center">
          <form
            id={mode === OnboardMode.REGISTER ? "form-phone-pin-register" : "form-phone-pin-login"}
            name={mode === OnboardMode.REGISTER ? "form-phone-pin-register" : "form-phone-pin-login"}
            onSubmit={handlePhoneSubmit((e) => delay().then(() => onSubmitPhone(e)))}
            className="w-full"
            onFocus={() => {
              if (mode === OnboardMode.REGISTER && !hasFocusedPhoneFormRef.current) {
                trackRegisterFormFocus(RegistrationMethod.PHONE_PIN);
                hasFocusedPhoneFormRef.current = true;
              }
            }}
          >
            <TextField
              {...formPhoneRegister("phone")}
              error={!!formPhoneErrors.phone}
              fieldHelperTextManager={fieldHelperTextManager}
              helperText={t(formPhoneErrors.phone?.message ?? "", {
                minLength: phoneMinLength,
                maxLength: phoneMaxLength,
                startWith: Array.isArray(phoneStartWith) ? phoneStartWith.join(", ") : "0",
              })}
              placeholder={t("Phone number")}
              type={"number"}
              inputMode={"numeric"}
              icon={<IconMSIPhoneAndroid className="text-content-base" />}
              classes={{
                "input&": "w-full !px-0 text-center",
                "iconContainer&": "!start-3 !top-[13px]",
                "prefixContainer&": showPhoneInputPrefix ? "absolute start-11 top-[13px] text-content-base" : "",
              }}
              helperTextClasses={{
                className: "!text-end",
              }}
              size="lg"
              aria-label="Onboard phone number input"
              prefix={showPhoneInputPrefix ? phonePrefix : undefined}
              data-element-id="input-onboard-phone"
            />

            <div
              className={cn("relative mt-6 w-full", {
                "!mt-7": showFreeGiftsBadge,
              })}
            >
              <FreeGiftsBadge
                visible={!loginCredId && mode === OnboardMode.REGISTER}
                className="-top-[22px]"
              />
              <Button
                id="btn-next"
                variant="primary"
                size="lg"
                type="submit"
                className="w-full"
                loading={isCreateRegisterLoading}
                aria-label="Onboard phone number submit"
                data-element-id="button-onboard-phone-submit"
              >
                {t("Next")}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* SOCIAL AUTH */}
      <AuthSocial
        mode={mode}
        title={t("or")}
        onLoginSuccess={postLoginCheck}
        className="mt-2"
      />
    </>
  );
};

export default OnboardPP;
